import { useState } from "react";
import { dcHandoff } from "@/lib/api";
import { track } from "@/lib/analytics";
import { FitPreviewMock } from "@/components/mockups/AppMock";
import { Lock } from "lucide-react";

const DEFAULT_LOCKED = ["What's helping", "What we'd watch", "Underwriter view", "Why this answer", "Best next move"];

export default function Conversion({ fit, scenario }) {
  const [handoff, setHandoff] = useState(null);
  const [busy, setBusy] = useState(false);

  const lockedRows = fit?.locked?.available_in_app?.slice(0, 5) || DEFAULT_LOCKED;

  const continueInApp = async () => {
    track("app_cta_clicked", { location: "conversion" });
    if (!fit || !scenario || busy) return;
    setBusy(true);
    try {
      const res = await dcHandoff({
        vehicle_price: scenario.vehicle_price,
        down_payment: scenario.down_payment,
        trade_equity: scenario.trade_equity,
        term_months: scenario.term_months,
        apr: fit.apr,
        vehicle_age_years: scenario.vehicle_age_years ?? 0,
        financials: {
          monthly_income: scenario.monthly_income,
          monthly_debts: scenario.monthly_debts,
          housing_cost: scenario.housing_cost,
          credit_score: scenario.credit_score,
        },
        fit_score: fit.fit_score,
        fit_status: fit.fit_status?.label,
        source: "homepage",
      });
      setHandoff(res.data);
      track("handoff_created", { page: "home" });
    } catch {
      /* handoff optional — deep link simply unavailable */
    } finally { setBusy(false); }
  };

  return (
    <section id="beyond" data-testid="conversion-section" className="py-20 bg-white border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
              Your Fit Score is only the beginning.
            </h2>
            <p className="text-stone-600 text-lg leading-relaxed mb-8">
              The website shows where this vehicle stands. DriveClear helps you understand why — and what you can do next.
            </p>

            <ul className="space-y-3 mb-8">
              {lockedRows.map((r) => (
                <li key={r} className="flex items-center gap-3 text-sm text-stone-500">
                  <span className="w-7 h-7 rounded-lg bg-stone-100 flex items-center justify-center text-stone-400">
                    <Lock className="w-3.5 h-3.5" />
                  </span>
                  {r}
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">In app</span>
                </li>
              ))}
            </ul>

            {handoff ? (
              <div data-testid="handoff-result" className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 mb-4">
                <p className="text-sm font-semibold text-stone-900 mb-1">Your scenario is ready in DriveClear.</p>
                <p className="text-xs text-stone-600 mb-3">Open the app and enter code <strong className="font-mono text-stone-900">{handoff.code}</strong> — no re-entering numbers.</p>
                <a href={handoff.deep_link} data-testid="handoff-deep-link" className="inline-flex items-center justify-center px-5 py-3 rounded-xl bg-stone-900 text-white text-sm font-semibold">
                  Understand My {fit.fit_score}% Fit in DriveClear
                </a>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={continueInApp}
                  disabled={busy}
                  data-testid="continue-in-app-button"
                  className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold text-sm transition-colors shadow-sm"
                >
                  {busy ? "Preparing..." : fit ? "Continue This Fit in DriveClear" : "Check Your Fit First"}
                </button>
                <a href="#get-app" data-testid="get-app-button" onClick={() => track("app_cta_clicked", { location: "conversion_secondary" })} className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl border-2 border-stone-200 text-stone-800 hover:border-stone-300 font-semibold text-sm transition-colors">
                  Get the DriveClear App
                </a>
              </div>
            )}
            {!fit && (
              <p className="text-xs text-stone-500 mt-3">Run the calculator above and your scenario carries over — no re-entering numbers.</p>
            )}
          </div>

          <div className="flex justify-center">
            <FitPreviewMock
              score={fit?.fit_score ?? 79}
              label={fit?.fit_status?.label ?? "Strong"}
              rows={fit?.locked?.available_in_app || []}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
